/**
 * Shared journey form page component
 * Used in both index.html and marketing-embed.html
 * 
 * This component handles the entire page layout including:
 * - Container structure
 * - Content layout (two-column responsive grid)
 * - Description content
 * - Form with conditional email field
 */

/**
 * Check if we're running in an iframe
 * @returns {boolean} - True if running in an iframe
 */
function isInIframe() {
  return window !== window.parent;
}

/**
 * Ensure CSS is loaded for the journey form page
 */
function ensureStylesLoaded() {
  // Check if we already loaded the styles
  if (document.getElementById('journey-form-page-styles')) {
    return;
  }
  
  // Create link element for our CSS
  const link = document.createElement('link');
  link.id = 'journey-form-page-styles';
  link.rel = 'stylesheet';
  link.href = '/css/components/journey-form-page.css';
  
  // Add to head
  document.head.appendChild(link);
}

/**
 * Adds safe area insets for mobile devices to ensure consistent padding
 * across different devices, especially those with notches or rounded corners
 */
function addSafeAreaStylesIfNeeded() {
  // Check if we already added the styles
  if (document.getElementById('journey-form-safe-area-styles')) {
    return;
  }
  
  // Create style element
  const styleEl = document.createElement('style');
  styleEl.id = 'journey-form-safe-area-styles';
  styleEl.textContent = `
    @supports (padding-top: env(safe-area-inset-top)) {
      body {
        padding-top: env(safe-area-inset-top);
        padding-bottom: env(safe-area-inset-bottom);
      }
    }
  `;
  
  // Add to head
  document.head.appendChild(styleEl);
}

/**
 * Create description content for journey form
 * @returns {HTMLElement} - The description content element
 */
function createDescriptionContent() {
  const content = document.createElement('div');
  content.id = 'descriptionContent';
  
  // Use simple styling without the content box or background
  content.className = 'w-full';
  
  // Add padding to the content instead of relying on journey-content-box
  content.style.padding = isInIframe() ? '1rem' : '0 2rem 2rem 2rem';
  
  // For anchoring in mobile iframe context
  if (isInIframe()) {
    content.id = 'descriptionAnchor';
  }
  
  content.innerHTML = `
    <h2 class="text-6xl font-normal text-gray-900 mb-6">Get Your Preview</h2>
    <div class="text-gray-700">
      <p class="text-lg mb-4">Ready to see how having an AI Advisor for your products changes the game? In 60 seconds, Polly will analyze your category, identify where customers typically drop off, and build a personalized preview—with sample conversations, strategic timing, and your projected retention lift.</p>
      <p class="text-lg mb-6"><strong>Here's how:</strong></p>
      <ol class="space-y-3 list-none">
        <li class="flex items-start">
          <span class="inline-block w-6 mr-3 text-brand-blue font-bold text-center flex-shrink-0">1.</span>
          <span><strong>Tell Polly about your product</strong><br>Just your brand name, product, and email—that's all she needs!</span>
        </li>
        <li class="flex items-start">
          <span class="inline-block w-6 mr-3 text-brand-blue font-bold text-center flex-shrink-0">2.</span>
          <span><strong>Polly analyzes your category</strong><br>She identifies typical churn patterns for products like yours, spots the moments where customers usually drop off, and pinpoints how to retain them.</span>
        </li>
        <li class="flex items-start">
          <span class="inline-block w-6 mr-3 text-brand-blue font-bold text-center flex-shrink-0">3.</span>
          <span><strong>Your preview loads instantly</strong><br>See the personalized conversations Polly would have on your behalf—in your brand's voice—along with strategic timing for each touchpoint and your potential retention lift.</span>
        </li>
      </ol>
    </div>
  `;
  return content;
}

/**
 * Create journey generation form
 * @param {string} id - Form ID
 * @param {boolean} isMarketingPath - Whether this is the marketing path
 * @returns {HTMLElement} - The form container element
 */
function createJourneyForm(id = 'journeyForm', isMarketingPath = false) {
  // Use the unified journey-form component if available
  if (window.journeyForm) {
    return window.journeyForm.create({ id, isMarketingPath });
  }

  // Fallback to original implementation for marketing path
  // (until we migrate that too)
  const form = document.createElement('div');

  // Add consistent ID and classes
  form.id = 'formContainer';
  form.className = 'journey-content-box w-full';

  // Form title section with Polly mascot
  const titleSection = document.createElement('div');
  titleSection.className = 'flex items-start mb-6 gap-4';
  titleSection.innerHTML = `
    <div class="flex-shrink-0">
      <img src="/img/polly-4.svg" alt="Polly the Kakapo" width="104" height="104">
    </div>
    <div class="flex-grow relative bg-white rounded-lg p-4 text-gray-800 text-sm" style="border-radius: 12px; ">
      <div style="position: absolute; left: -8px; top: 20px; width: 0; height: 0; border-top: 8px solid transparent; border-bottom: 8px solid transparent; border-right: 8px solid white;"></div>
      <span class="font-semibold">Hi! I'm Polly 👋</span> Most brands lose 60% of customers after their first purchase. Want to see how I'd keep YOURS engaged? Tell me what you sell!
    </div>
  `;

  // Create form element
  const formElement = document.createElement('form');
  formElement.id = id;
  formElement.className = 'space-y-6';
  if (!isMarketingPath) {
    formElement.action = '/generate';
    formElement.method = 'POST';
  }

  // Company field
  const companyField = document.createElement('div');
  companyField.innerHTML = `
    <label for="company" class="block text-base font-medium text-brand-white">Your Company Name</label>
    <input type="text" id="company" name="company" required
      class="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-brand-blue focus:border-brand-blue">
  `;

  // Product field
  const productField = document.createElement('div');
  productField.innerHTML = `
    <label for="product" class="block text-base font-medium text-brand-white">Your Product</label>
    <input type="text" id="product" name="product" required
      class="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-brand-blue focus:border-brand-blue">
  `;

  // Template field (sales path only — marketing defaults to 'activate' server-side)
  let templateField;
  if (!isMarketingPath) {
    templateField = document.createElement('div');
    templateField.innerHTML = `
      <label for="templateType" class="block text-base font-medium text-brand-white">Journey Type</label>
      <select id="templateType" name="templateType"
        class="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-brand-blue focus:border-brand-blue bg-white">
        <option value="activate">Activate</option>
        <option value="engage">Engage</option>
      </select>
    `;
  }

  // Email field (optional)
  let emailField;
  let termsField;
  if (isMarketingPath) {
    emailField = document.createElement('div');
    emailField.innerHTML = `
      <label for="email" class="block text-base font-medium text-brand-white">Your Email</label>
      <input type="email" id="email" name="email" required
        class="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-brand-blue focus:border-brand-blue">
    `;

    // Terms and conditions checkbox field
    termsField = document.createElement('div');
    termsField.innerHTML = `
      <div class="flex items-start">
        <input type="checkbox" id="termsCheckbox" name="termsAccepted" required
          class="terms-checkbox">
        <label for="termsCheckbox" class="ml-2 text-sm text-brand-white">
          I agree to the <a href="https://blustream.io/terms-of-service" target="_blank" class="text-brand-white underline hover:text-brand-light-blue">terms and conditions</a>.
        </label>
      </div>
    `;
  }

  // Submit button
  const buttonField = document.createElement('div');
  buttonField.innerHTML = `
    <button type="submit" id="generateButton" class="brand-button">
      <div class="flex items-center justify-center">
        <span id="buttonText" class="inline-block text-center whitespace-nowrap overflow-hidden">Generate a journey →</span>
        <svg id="loadingSpinner" class="animate-spin ml-2 h-4 w-4 text-white hidden" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      </div>
    </button>
  `;

  // Assemble form
  formElement.appendChild(companyField);
  formElement.appendChild(productField);
  if (templateField) {
    formElement.appendChild(templateField);
  }
  if (isMarketingPath) {
    formElement.appendChild(emailField);
    formElement.appendChild(termsField);
  }
  formElement.appendChild(buttonField);

  // Add title and form to container
  form.appendChild(titleSection);
  form.appendChild(formElement);

  return form;
}

/**
 * Render the complete form page including layout structure
 * Uses standardized CSS classes for layout
 * 
 * @param {string} containerId - ID of the container element
 * @param {boolean} isMarketingPath - Whether this is the marketing path
 */
function renderFullFormPage(containerId, isMarketingPath = false) {
  const pageContainer = document.getElementById(containerId);
  if (!pageContainer) return;
  
  // Ensure our stylesheet is loaded
  ensureStylesLoaded();
  
  // Add safe area styles for mobile devices
  addSafeAreaStylesIfNeeded();
  
  // Create the main container
  const layoutContainer = document.createElement('div');
  layoutContainer.className = 'journey-layout-container';
  
  // Add iframe class if we're in an iframe
  if (isInIframe()) {
    layoutContainer.classList.add('in-iframe');
  }
  
  // Create the content row
  const contentRow = document.createElement('div');
  contentRow.className = 'journey-content-row';
  
  // Create left column for description content
  const descriptionCol = document.createElement('div');
  descriptionCol.className = 'journey-column';
  descriptionCol.appendChild(createDescriptionContent());
  
  // Create right column for form
  const formCol = document.createElement('div');
  formCol.className = 'journey-column';
  formCol.appendChild(createJourneyForm('journeyForm', isMarketingPath));
  
  // Assemble the structure
  contentRow.appendChild(descriptionCol);
  contentRow.appendChild(formCol);
  layoutContainer.appendChild(contentRow);
  
  // Add to the page
  pageContainer.appendChild(layoutContainer);
  
  // Trigger a custom event to notify that the form has been rendered
  const event = new CustomEvent('journeyFormRendered');
  document.dispatchEvent(event);
}

// Export functions as ES modules
export { 
  createDescriptionContent, 
  createJourneyForm, 
  renderFullFormPage, 
  addSafeAreaStylesIfNeeded,
  isInIframe 
};

// Also make available globally for backward compatibility during transition
window.journeyFormPage = {
  createDescriptionContent,
  createJourneyForm,
  renderFullFormPage,
  addSafeAreaStylesIfNeeded
};